import React, { useLayoutEffect, useState } from 'react';
import Lottie from 'lottie-react';
import defaultLoaderAnimation from '../../assets/lottie/generic-loader.json';

const SIZES = {
  small: { width: 80, height: 80 },
  medium: { width: 120, height: 120 },
  large: { width: 450, height: 450 },
};

// Synchronously read the sidebar width before paint to avoid flash
const getContentLeft = () => {
  if (typeof document === 'undefined') return 0;
  const mainContent = document.querySelector('.mainContent');
  return mainContent ? mainContent.getBoundingClientRect().left : 0;
};

/**
 * LottieLoader — drop-in replacement for Ant Design <Spin>.
 *
 * Standalone:  <LottieLoader size="large" text="Loading..." />
 * Wrapper:     <LottieLoader spinning={loading} text="Loading...">content</LottieLoader>
 * Inline:      <LottieLoader inline spinning={loading}>content</LottieLoader>
 * Custom:      <LottieLoader animationData={myLottie} size="medium" />
 */
const LottieLoader = ({
  animationData = defaultLoaderAnimation,
  size = 'large',
  text,
  spinning,
  children,
  style,
  inline = false,
}) => {
  const dimensions = typeof size === 'number' ? { width: size, height: size } : (SIZES[size] || SIZES.large);
  const [contentLeft, setContentLeft] = useState(() => (inline ? 0 : getContentLeft()));

  useLayoutEffect(() => {
    if (inline) return undefined;
    setContentLeft(getContentLeft());
    const handleResize = () => setContentLeft(getContentLeft());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [inline, spinning]);

  const lottieElement = (
    <Lottie
      animationData={animationData}
      loop
      autoplay
      style={{ width: dimensions.width, height: dimensions.height }}
    />
  );

  const overlayBase = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
  };

  const fullScreenOverlay = { ...overlayBase, position: 'fixed', top: 0, left: contentLeft, right: 0, bottom: 0, zIndex: 9999 };

  const inlineContent = (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '40px 0', ...style }}>
      {lottieElement}
      {text && <span style={{ fontSize: 14, color: '#485066', fontWeight: 500 }}>{text}</span>}
    </div>
  );

  // Wrapper mode: <LottieLoader spinning={bool}>children</LottieLoader>
  if (children !== undefined) {
    if (!spinning) return <>{children}</>;
    if (inline) return inlineContent;
    return (
      <div style={{ ...style }}>
        {children}
        <div style={fullScreenOverlay}>
          {lottieElement}
          {text && <span style={{ fontSize: 14, color: '#485066', fontWeight: 500 }}>{text}</span>}
        </div>
      </div>
    );
  }

  // Standalone mode: <LottieLoader size="large" text="Loading..." />
  if (inline) return inlineContent;
  return (
    <div style={style}>
      <div style={fullScreenOverlay}>
        {lottieElement}
        {text && <span style={{ fontSize: 14, color: '#485066', fontWeight: 500 }}>{text}</span>}
      </div>
    </div>
  );
};

export default LottieLoader;
